import { useEffect, useState, type ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";

import {
  getRetirementGateAction,
  hasChosenToViewOldApp,
  MOVED_NOTICE_PATH,
  readStoredTenantRetirement,
  TENANT_RETIREMENT_EVENT,
} from "@/utils/tenant-retirement";
import { useTenantBranding } from "@/hooks/useTenantBranding";

function useRetirementState() {
  const [state, setState] = useState(() => ({
    retirement: readStoredTenantRetirement(),
    viewOldApp: hasChosenToViewOldApp(),
  }));
  useEffect(() => {
    const refresh = () =>
      setState({ retirement: readStoredTenantRetirement(), viewOldApp: hasChosenToViewOldApp() });
    window.addEventListener(TENANT_RETIREMENT_EVENT, refresh);
    return () => window.removeEventListener(TENANT_RETIREMENT_EVENT, refresh);
  }, []);
  return state;
}

/**
 * Inside the signed-in layout only (receipt and form pages are outside it). A tenant that moved to
 * Jamaat360 is sent to the notice screen; a platform admin who chose "View the old app" sees a banner.
 */
export default function RetirementGate(): ReactElement | null {
  const { retirement, viewOldApp } = useRetirementState();
  const { pathname } = useLocation();
  const { tenantName } = useTenantBranding();
  const action = getRetirementGateAction(retirement, viewOldApp, pathname);

  if (action === "redirect-to-notice") {
    return <Navigate to={MOVED_NOTICE_PATH} replace />;
  }
  if (action === "banner" && retirement) {
    return (
      <Alert
        severity="warning"
        square
        sx={{ position: "sticky", top: 0, zIndex: (theme) => theme.zIndex.appBar + 1 }}
      >
        Read-only copy. {tenantName || "This jamaat"} has moved to Jamaat360.{" "}
        <Link href={retirement.newAppUrl} color="inherit" underline="always">
          Go to the new app
        </Link>
      </Alert>
    );
  }
  return null;
}
